import { useMemo, useState } from 'react';
import { Pencil, Plus, Trash2 } from 'lucide-react';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import type { ApiError } from '@/core/api';
import { useEmployees } from '@/features/hrm/employees/queries';
import { employeeName } from '@/features/hrm/employees/types';
import { TaxProfileFormDialog } from '../components/TaxProfileFormDialog';
import {
  useCreateTaxProfile,
  useDeleteTaxProfile,
  useTaxProfiles,
  useUpdateTaxProfile,
} from '../queries';
import type { TaxProfile, TaxProfileInput } from '../types';

/** True when the server masked the field (C7 — comes back as "••••"). */
function isMasked(value: string): boolean {
  return /^•+$/.test(value);
}

export function TaxesListPage() {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<TaxProfile | undefined>(undefined);
  const [toDelete, setToDelete] = useState<TaxProfile | null>(null);

  const { data, isPending, isError, error, isFetching } = useTaxProfiles();
  const { data: employeesData } = useEmployees({});
  const employeeLabels = useMemo(() => {
    const map = new Map<number, string>();
    for (const e of employeesData?.results ?? []) map.set(e.id, employeeName(e));
    return map;
  }, [employeesData]);

  const create = useCreateTaxProfile();
  const update = useUpdateTaxProfile();
  const remove = useDeleteTaxProfile();

  const profiles = data?.results ?? [];

  async function handleSubmit(input: TaxProfileInput) {
    if (editing) await update.mutateAsync({ id: editing.id, input });
    else await create.mutateAsync(input);
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Taxes</h1>
          <p className="text-muted-foreground text-sm">
            Record-only tax data per employee. No calculation engine (ADR-009).
          </p>
        </div>
        <Button
          onClick={() => {
            setEditing(undefined);
            setDialogOpen(true);
          }}
        >
          <Plus className="size-4" /> New tax profile
        </Button>
      </div>

      {isError ? (
        <p className="text-destructive text-sm">
          {(error as unknown as ApiError)?.message ?? 'Failed to load tax profiles.'}
        </p>
      ) : (
        <div className="rounded-md border" aria-busy={isFetching}>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Employee</TableHead>
                <TableHead>Country</TableHead>
                <TableHead>Tax ID</TableHead>
                <TableHead>Recorded rates</TableHead>
                <TableHead className="w-24 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isPending ? (
                Array.from({ length: 3 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell colSpan={5}>
                      <Skeleton className="h-6 w-full" />
                    </TableCell>
                  </TableRow>
                ))
              ) : profiles.length ? (
                profiles.map((p) => {
                  const masked = isMasked(p.tax_id);
                  const rateKeys = Object.keys(p.recorded_rates ?? {});
                  return (
                    <TableRow key={p.id}>
                      <TableCell className="font-medium">
                        {employeeLabels.get(p.employee) ?? `#${p.employee}`}
                      </TableCell>
                      <TableCell>
                        {p.country ? <Badge variant="outline">{p.country}</Badge> : '—'}
                      </TableCell>
                      <TableCell className={masked ? 'text-muted-foreground tabular-nums' : 'tabular-nums'}>
                        {p.tax_id || '—'}
                        {masked ? (
                          <span className="text-muted-foreground ml-2 text-xs">(restricted)</span>
                        ) : null}
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm">
                        {rateKeys.length ? rateKeys.join(', ') : '—'}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="icon"
                          variant="ghost"
                          aria-label="Edit tax profile"
                          onClick={() => {
                            setEditing(p);
                            setDialogOpen(true);
                          }}
                        >
                          <Pencil className="size-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          aria-label="Delete tax profile"
                          onClick={() => setToDelete(p)}
                        >
                          <Trash2 className="size-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={5} className="text-muted-foreground text-center">
                    No tax profiles recorded.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      )}

      <TaxProfileFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        profile={editing}
        onSubmit={handleSubmit}
      />

      <AlertDialog open={Boolean(toDelete)} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete tax profile?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes the recorded tax data for{' '}
              {toDelete ? employeeLabels.get(toDelete.employee) ?? 'this employee' : ''}.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (toDelete) remove.mutate(toDelete.id);
                setToDelete(null);
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
